import { GlobalTypography, Image, Product, ProductCardBlock, ProductPage } from 'cms-types'
import { Product as ShopifyProduct } from 'shopify-types'
import { getShopifyGID } from 'shopify-types/helpers'
import { ProductForProductCard, ProductInfo } from 'ui'
import { SetCreatorProduct } from 'ui/modules/SetCreator'

import { getCollectionItem, getGlobalTypography } from '@/cms'
import { pageToUrl } from '@/cms/helpers'
import { fetchProductReviewRatings } from '@/helpers/fetchProductReviewRatings'
import { getVariantPricing } from '@/helpers/getVariantPricing'
import { shopify } from '@/shopify'

import { logger } from './logger'
import { ProductVariantWithProductId } from './mapProductVariants'
import { getTextFromRichText } from './richTextParser'

type ProductWithData = Omit<ProductCardBlock, 'productVariant'> & {
  productVariant: NonNullable<Product['variants']>[number]
  product: Product
}

type OutOfStockTag = ProductForProductCard['outOfStockTag']

const mapOutOfStockTag = (
  tag: GlobalTypography['productPage']['outOfStockTag'],
): OutOfStockTag => ({
  title: tag.title,
  icon: tag.icon ?? undefined,
})

export const getProductCardsForCMSProductCardBlocks = async (
  products: ProductCardBlock[],
  locale: string,
): Promise<ProductForProductCard[]> => {
  const productsWithData = await Promise.all(
    products.map(async ({ productVariant, page, ...restData }) => {
      const productPage = page?.value as ProductPage
      const product = productPage?.product as Product

      if (!product) {
        return null
      }

      const variant = product.variants?.find(({ id, shopifyId }) =>
        [id, shopifyId].includes(productVariant),
      )

      return {
        page,
        product,
        productVariant: variant,
        ...restData,
      }
    }),
  )

  const filteredProductsWithData = productsWithData.filter(Boolean) as ProductWithData[]

  const productIds = Array.from(
    new Set(filteredProductsWithData.map(({ product }) => product.shopifyId)),
  ).map(id => getShopifyGID(id, 'Product'))

  try {
    const globalTypography = await getGlobalTypography(locale)
    const shopifyProducts = await shopify({
      language: locale.toUpperCase(),
      country: locale.toUpperCase() === 'PL' ? 'PL' : 'DE',
    }).product.fetchMultiple(productIds)

    const shopifyProductsMap = new Map(shopifyProducts.map(product => [product.id, product]))

    const mappedProducts = await Promise.all(
      mapCMSProductCardBlocksToProductCards(
        filteredProductsWithData,
        shopifyProductsMap,
        locale,
        mapOutOfStockTag(globalTypography.productPage.outOfStockTag),
      ),
    )

    return mappedProducts
      .filter(Boolean)
      .filter(product => product?.shown) as ProductForProductCard[]
  } catch (e) {
    logger.error('Error fetching shopify products', e)
    throw e
  }
}

export const mapCMSProductVariantsToProductCards = async (
  productVariants: ProductVariantWithProductId[] = [],
  locale: string,
) => {
  const productBlocks: ProductCardBlock[] = productVariants.map(productVariant => ({
    blockType: 'ProductCard',
    productVariant: productVariant.id as string,
    page: {
      value: productVariant.productPage,
      relationTo: 'productPages',
    },
  }))

  return getProductCardsForCMSProductCardBlocks(productBlocks, locale)
}

export const mapCMSProductVariantsToSetCreatorProducts = async (
  products: Array<Product & { selectedVariantId: string }>,
  locale: string,
): Promise<{ currency: string; products: SetCreatorProduct[] }> => {
  let currency = ''
  const globalTypography = await getGlobalTypography(locale)
  const outOfStockTag = mapOutOfStockTag(globalTypography.productPage.outOfStockTag)

  // Fetch the variants for each product
  const fetchedVariants = await Promise.all(
    products?.map(async product => {
      const {
        shopifyId: productId,
        selectedVariantId,
        variants: productVariants,
        ...rest
      } = product

      let variants: ShopifyProduct['variants']
      let title: string
      try {
        ;({ variants, title } = await shopify({
          language: locale.toUpperCase(),
          country: locale.toUpperCase() === 'PL' ? 'PL' : 'DE',
        }).product.fetch(getShopifyGID(productId, 'Product')))
      } catch (error) {
        // A product may not be published to every market (e.g. missing for PL
        // while present for DE) — skip it for this locale instead of failing
        // the whole page/build.
        logger.error(`Set creator product unavailable for locale "${locale}"`, error)
        return null
      }

      const currentShopifyVariant = selectedVariantId
        ? variants.find(variant => variant.id.includes(selectedVariantId))
        : null

      if (currentShopifyVariant == null) {
        return null
      }

      const { price, id, compareAtPrice } = currentShopifyVariant

      if (currency.length === 0) {
        currency = price.currencyCode
      }

      const variantNameOption = currentShopifyVariant.selectedOptions.find(
        ({ name }) => name === 'Name',
      )

      const selectedVariant: NonNullable<SetCreatorProduct['variants']>[0] | undefined =
        productVariants?.find(({ shopifyId }) => {
          return shopifyId === selectedVariantId
        })

      const variantImage = selectedVariant?.productInfo?.images[0].image
      let fetchedImage: Image | null = null
      if (typeof variantImage === 'string') {
        fetchedImage = await getCollectionItem(variantImage, 'images')
      }

      const overviewDescription = getTextFromRichText(
        selectedVariant?.productInfo?.overviewDescription,
      )

      return {
        shopifyId: productId,
        description:
          overviewDescription !== ''
            ? overviewDescription
            : getTextFromRichText(selectedVariant?.productInfo?.description),
        productId,
        variants: selectedVariant ? [selectedVariant] : [],
        ...rest,
        image: fetchedImage || variantImage,
        variantId: id,
        title: variantNameOption ? `${title} ${variantNameOption.value}` : title,
        price: price.amount.toString(),
        currency: price.currencyCode,
        compareAtPrice:
          compareAtPrice && compareAtPrice.amount > price.amount
            ? { ...compareAtPrice }
            : undefined,
        imageAlt: title,
        pageUrl: `/products/${productId}`,
        isAvailable: currentShopifyVariant.availableForSale,
        outOfStockTag,
      }
    }) ?? [],
  )

  const filteredVariant = fetchedVariants.filter(Boolean) as unknown as SetCreatorProduct[]

  return {
    currency,
    products: filteredVariant,
  }
}

const mapCMSProductCardBlocksToProductCards = (
  filteredProductsWithData: ProductWithData[],
  shopifyProductsMap: Map<string, ShopifyProduct>,
  locale: string,
  outOfStockTag: OutOfStockTag,
) => {
  return filteredProductsWithData.map(async ({ productVariant, page, product, tag }) => {
    const productId = product.shopifyId

    const shopifyProduct = shopifyProductsMap.get(`gid://shopify/Product/${productId}`)

    if (shopifyProduct == null) return null

    const { title, variants } = shopifyProduct

    const variant = variants.find(({ id }) => id.includes(productVariant?.shopifyId as string))

    if (variant == null) return null

    const [firstImage, hoverImage] = await Promise.all(
      (productVariant?.productInfo?.images as NonNullable<ProductInfo['images']>).map(
        async ({ image }) => {
          if (typeof image === 'string') {
            return await getCollectionItem(image, 'images', {
              searchParams: { locale },
            })
          }

          return image
        },
      ),
    )

    const volume = variant.selectedOptions.find(({ name }) => name === 'Size')
    const variantNameOption = variant.selectedOptions.find(({ name }) => name === 'Name')

    const productDetails = page.value as ProductPage

    const [firstPDPImage] =
      productDetails.productIntro?.variantImages?.find(
        ({ variant }) => variant === productVariant?.id,
      )?.images ?? []

    const productTag = tag?.isPriority ? tag : (firstPDPImage?.tag ?? tag)
    const overviewDescription = getTextFromRichText(
      productVariant?.productInfo?.overviewDescription,
    )

    const blackFridayPrice = productVariant?.productInfo?.blackFridayPrice

    const { price: usedPrice, compareAtPrice: usedCompareAtPrice } = getVariantPricing({
      price: variant.price,
      compareAtPrice: variant.compareAtPrice,
      blackFridayPrice,
    })

    const reviews = await fetchProductReviewRatings(variant.sku)

    return {
      title: variantNameOption ? `${title} ${variantNameOption.value}` : title,
      description:
        overviewDescription !== ''
          ? overviewDescription
          : getTextFromRichText(productVariant?.productInfo?.description),
      productId,
      variantId: variant.id,
      image: firstImage as Image,
      hoverImage,
      imageAlt: title,
      isAvailable: 'availableForSale' in variant ? variant.availableForSale : false,
      outOfStockTag,
      volume: volume?.value,
      price: usedPrice.amount.toString(),
      currency: variant.price.currencyCode,
      compareAtPrice: usedCompareAtPrice,
      pageUrl: pageToUrl(page) ?? '',
      tag: productTag,
      reviews,
      shown: !variant.hidden,
    }
  })
}
